/**
 * A destructive action confirmed by typing a phrase - "Delete Project" - exactly. A click on
 * "Delete" and another on "Delete permanently" is two clicks a hand can make by accident; a
 * phrase typed out is not. The button stays disabled until the phrase matches.
 */

import { useState } from 'react';
import { Button } from './Button';

/** Whether what was typed is the phrase: exactly, case included, surrounding spaces ignored. */
export function phraseMatches(typed: string, phrase: string): boolean {
  return typed.trim() === phrase;
}

export function TypedConfirm({
  phrase,
  message,
  action,
  onConfirm,
  onCancel,
}: {
  /** What must be typed, exactly (case included). */
  phrase: string;
  /** What will happen, said plainly. */
  message: string;
  /** The button's label. */
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const matches = phraseMatches(typed, phrase);
  return (
    <span
      role="group"
      aria-label={action}
      className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-gray-700"
      onClick={(e) => e.stopPropagation()}
    >
      <span>
        {message} Type <strong className="font-semibold text-gray-900">{phrase}</strong> to confirm.
      </span>
      <input
        autoFocus
        aria-label={`Type ${phrase} to confirm`}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && matches) onConfirm();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder={phrase}
        className="w-32 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
      />
      <Button size="sm" variant="danger" disabled={!matches} onClick={onConfirm}>
        {action}
      </Button>
      <Button size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </span>
  );
}
